import React, { useState, useEffect } from "react";
import { useGetUserStatsQuery, useUpdateUserStatsMutation } from "../../api";
import { UserFullStats } from "../../types/user";
import { useNotification } from "../../hooks/useNotification";

interface UserStatsModalProps {
    isOpen: boolean;
    onClose: () => void;
    userId: number | null;
    onSaved?: () => void;
}

const UserStatsModal: React.FC<UserStatsModalProps> = ({
    isOpen,
    onClose,
    userId,
    onSaved,
}) => {
    const { showNotification } = useNotification();
    const [formData, setFormData] = useState<Partial<UserFullStats>>({});
    
    // RTK Query hooks
    const { data: statsData, isLoading, isFetching } = useGetUserStatsQuery(userId!, { skip: !isOpen || !userId });
    const [updateStats, { isLoading: isSaving }] = useUpdateUserStatsMutation();

    useEffect(() => {
        if (isOpen && statsData?.data) {
            setFormData(statsData.data);
        } else if (!isOpen) {
            setFormData({});
        }
    }, [isOpen, statsData]);

    if (!isOpen) return null;

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value === "" ? null : value,
        }));
    };

    const handleSave = async () => {
        if (!userId) return;
        try {
            await updateStats({ userId, data: formData }).unwrap();
            showNotification("Статистика успешно обновлена", "success");
            if (onSaved) onSaved();
            onClose();
        } catch (error: any) {
            showNotification(
                error?.data?.message || error?.message || "Ошибка сохранения статистики",
                "error"
            );
        }
    };

    const renderInput = (name: keyof UserFullStats, label: string, type = "number") => {
        const val = formData[name];
        // Handle level_counts as string if it's an object from API
        const displayVal = (name === 'level_counts' && typeof val === 'object' && val !== null) 
            ? JSON.stringify(val) 
            : (val !== null && val !== undefined ? String(val) : "");

        return (
            <div className="flex flex-col mb-4 w-full sm:w-1/2 md:w-1/3 px-2">
                <label className="text-sm text-gray-600 font-medium mb-1">
                    {label}
                </label>
                <input
                    type={type}
                    name={name as string}
                    value={displayVal}
                    onChange={handleInputChange}
                    className="border border-gray-300 rounded px-3 py-2 w-full focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
            </div>
        );
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
                className="fixed inset-0 bg-black/50 transition-opacity"
                onClick={onClose}
            ></div>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col z-10 overflow-hidden relative">
                <div className="p-4 sm:p-6 border-b border-gray-200 flex justify-between items-center bg-gray-50">
                    <h3 className="text-xl font-semibold text-gray-800">
                        {isLoading || isFetching ? "Загрузка..." : `Статистика пользователя #${userId}`}
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        disabled={isSaving}
                    >
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="p-4 sm:p-6 overflow-y-auto flex-1">
                    {isLoading || isFetching ? (
                        <div className="flex justify-center items-center py-10">
                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {/* 0. История статусов доступа */}
                            {statsData?.data?.status_history && statsData.data.status_history.length > 0 && (
                                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                                    <h4 className="font-semibold text-lg text-gray-800 border-b pb-2 mb-4">
                                        0. История изменения статусов доступа
                                    </h4>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full text-sm">
                                            <thead>
                                                <tr className="text-left text-gray-600 border-b">
                                                    <th className="py-1 pr-4">Дата</th>
                                                    <th className="py-1 pr-4">Статус</th>
                                                    <th className="py-1 pr-4">Длительность пакета (мес)</th>
                                                    <th className="py-1">Комментарий</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {statsData.data.status_history.map((item, idx) => (
                                                    <tr key={idx} className="border-b last:border-b-0">
                                                        <td className="py-1 pr-4 text-gray-800">
                                                            {item.created_at ? new Date(item.created_at).toLocaleString() : "-"}
                                                        </td>
                                                        <td className="py-1 pr-4">
                                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                                                {item.status}
                                                            </span>
                                                        </td>
                                                        <td className="py-1 pr-4 text-gray-800">
                                                            {item.count_pack_duration}
                                                        </td>
                                                        <td className="py-1 text-gray-700">
                                                            {item.comment || "-"}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* 1. Реферальная система */}
                            <div className="border border-gray-200 rounded-lg p-4">
                                <h4 className="font-semibold text-lg text-blue-800 border-b pb-2 mb-4">1. Реферальная система</h4>
                                <div className="flex flex-wrap -mx-2">
                                    {renderInput("clients_total", "Clients (Total)")}
                                    {renderInput("clients_month", "Clients (Month)")}
                                    {renderInput("dillers_total", "Dealers (Total)")}
                                    {renderInput("dillers_month", "Dealers (Month)")}
                                    {renderInput("managers_total", "Managers (Total)")}
                                    {renderInput("managers_month", "Managers (Month)")}
                                    {renderInput("new_referrals_count", "New Referrals Count")}
                                    {renderInput("total_referrals", "Total Referrals")}
                                    {renderInput("total_referrals_month", "Total Referrals (Month)")}
                                    {renderInput("pack_duration_months", "Pack Duration (Months)")}
                                    {renderInput("level_counts", "Level Counts (JSON/Text)", "text")}
                                </div>
                            </div>

                            {/* 2. Благотворительная программа */}
                            <div className="border border-gray-200 rounded-lg p-4">
                                <h4 className="font-semibold text-lg text-green-800 border-b pb-2 mb-4">2. Благотворительная программа</h4>
                                <div className="flex flex-wrap -mx-2">
                                    {renderInput("charity_spent", "Charity Spent")}
                                    {renderInput("charity_earned_month", "Charity Earned (Month)")}
                                    {renderInput("charity_earned_month_usdt", "Charity Earned USDT (Month)")}
                                    {renderInput("charity_earned_total", "Charity Earned (Total)")}
                                    {renderInput("charity_participations_total", "Charity Participations (Total)")}
                                    {renderInput("charity_participations_month", "Charity Participations (Month)")}
                                    {renderInput("charity_coeff", "Charity Coeff", "text")}
                                </div>
                            </div>

                            {/* 3. Звездная программа */}
                            <div className="border border-gray-200 rounded-lg p-4">
                                <h4 className="font-semibold text-lg text-yellow-600 border-b pb-2 mb-4">3. Звездная программа</h4>
                                <div className="flex flex-wrap -mx-2">
                                    {renderInput("star_users_total", "Star Users (Total)")}
                                    {renderInput("star_withdrawn_total", "Star Withdrawn (Total)")}
                                    {renderInput("star_earned_month", "Star Earned (Month)")}
                                    {renderInput("star_earned_month_usdt", "Star Earned USDT (Month)")}
                                    {renderInput("star_earned_total", "Star Earned (Total)")}
                                </div>
                            </div>

                            {/* 4. Ваучеры */}
                            <div className="border border-gray-200 rounded-lg p-4">
                                <h4 className="font-semibold text-lg text-purple-800 border-b pb-2 mb-4">4. Ваучеры</h4>
                                <div className="flex flex-wrap -mx-2">
                                    {renderInput("shares_count", "Shares Count")}
                                    {renderInput("shares_earned_month", "Shares Earned (Month)", "text")}
                                    {renderInput("shares_earned_month_usdt", "Shares Earned USDT (Month)", "text")}
                                    {renderInput("shares_earned_total", "Shares Earned (Total)", "text")}
                                    {renderInput("shares_earned_total_usdt", "Shares Earned USDT (Total)", "text")}
                                </div>
                            </div>

                            {/* 5. Компания */}
                            <div className="border border-gray-200 rounded-lg p-4">
                                <h4 className="font-semibold text-lg text-indigo-800 border-b pb-2 mb-4">5. Компания</h4>
                                <div className="flex flex-wrap -mx-2">
                                    {renderInput("companies_total", "Companies (Total)")}
                                    {renderInput("companies_month", "Companies (Month)")}
                                    {renderInput("companies_earned_total", "Companies Earned (Total)", "text")}
                                    {renderInput("companies_earned_month", "Companies Earned (Month)", "text")}
                                </div>
                            </div>

                        </div>
                    )}
                </div>

                <div className="p-4 sm:p-6 border-t border-gray-200 flex justify-end gap-3 bg-gray-50">
                    <button
                        onClick={onClose}
                        disabled={isSaving}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
                    >
                        Отмена
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={isLoading || isSaving}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center disabled:opacity-50"
                    >
                        {isSaving ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Сохранение...
                            </>
                        ) : (
                            "Сохранить"
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UserStatsModal;
